<template>
  <div>
    <v-btn outlined color="primary" :disabled="!votingId" @click="open">Termos de adesão</v-btn>
    <v-dialog v-model="dialog" max-width="860" :persistent="saving">
      <v-card class="ui-card">
        <div class="ui-card__header">
          <div>
            <h3 class="ui-section-title">Votos por termo de adesão</h3>
            <p class="ui-section-description">Termos assinados que antecipam o voto do credor nesta votação.</p>
          </div>
          <v-btn icon small aria-label="Fechar termos de adesão" :disabled="saving" @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <div class="ui-card__body">
          <v-alert type="info" text>O termo antecipa o voto nesta votação. Após registrado, nem o credor, nem o procurador, nem a administração poderão alterar ou restaurar esse voto. Em votação simples, a opção vale para todas as classes participantes do credor.</v-alert>
          <v-alert v-if="error" type="error" text>{{ error }}</v-alert>
          <template v-if="planAdhesion && planAdhesion.managed">
            <v-alert type="success" text>
              Este caso usa o módulo <strong>Adesão ao Plano</strong>: as adesões aprovadas entram nesta votação como voto "Sim" irrevogável.
              Votações ainda não iniciadas são sincronizadas automaticamente; nas demais, use o botão abaixo.
            </v-alert>
            <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px;">
              <ui-badge v-for="(count, key) in planAdhesion.counts" :key="key" :color="planStatus[key] ? planStatus[key].color : 'grey'" :text="`${planStatus[key] ? planStatus[key].label : key}: ${count}`" />
              <ui-badge v-if="planAdhesion.stale" tone="danger" :text="`Deixaram de valer: ${planAdhesion.stale}`" />
              <v-spacer />
              <v-btn v-if="(planAdhesion.writable) && $allowed('create')" color="primary" depressed small :loading="syncing" :disabled="!pendingSync" @click="syncPlanAdhesions">
                Importar adesões{{ pendingSync ? ` (${pendingSync})` : '' }}
              </v-btn>
            </div>
            <v-simple-table dense class="mb-4">
              <thead><tr><th>Credor</th><th>Termo(s)</th><th>Situação</th></tr></thead>
              <tbody>
                <tr v-for="head in planAdhesion.heads" :key="head.term_ids.join('-')">
                  <td><span class="ui-cell-main">{{ head.name }}</span><div class="ui-cell-sub">{{ head.registration_masked }} · Classe {{ head.class_type }}</div></td>
                  <td class="text-caption">{{ head.document_codes.join(', ') }}</td>
                  <td>
                    <ui-badge :color="planStatus[head.status] ? planStatus[head.status].color : 'grey'" :text="planStatus[head.status] ? planStatus[head.status].label : head.status" />
                    <div v-if="head.reason" class="ui-cell-sub">{{ head.reason }}</div>
                  </td>
                </tr>
                <tr v-if="!planAdhesion.heads.length"><td colspan="3" class="ui-muted">Nenhuma adesão aprovada no módulo.</td></tr>
              </tbody>
            </v-simple-table>
          </template>
          <v-form v-if="[1,2,3].includes(Number(status)) && !(planAdhesion && planAdhesion.managed)" ref="form" @submit.prevent="save">
            <v-autocomplete v-model="creditId" :items="credits" :search-input.sync="search" :loading="searching" item-value="id" :item-text="creditLabel" label="Crédito do caso" outlined dense no-filter :rules="[required]" @update:search-input="searchCredits" />
            <v-select v-model="vote" :items="choices" label="Voto declarado no termo" outlined dense :rules="[required]" />
            <v-file-input v-model="file" accept="application/pdf" label="Termo assinado (PDF, até 10 MB)" outlined dense :rules="[required]" />
            <v-checkbox v-model="confirmed" label="Conferi o credor, a votação e a opção no documento assinado. Confirmo o registro irrevogável." :rules="[required]" />
            <v-btn v-if="$allowed('create')" type="submit" color="primary" depressed :loading="saving">Registrar termo e voto</v-btn>
          </v-form>
          <v-divider class="my-6" />
          <h3 class="ui-section-title mb-3">Termos registrados</h3>
          <v-progress-linear v-if="loading" indeterminate />
          <p v-if="!loading && !terms.length" class="ui-muted">Nenhum termo registrado nesta votação.</p>
          <v-card v-for="term in terms" :key="term.id" outlined class="pa-3 mb-3">
            <div class="d-flex align-center"><span class="ui-cell-main">{{ snapshot(term).name }}</span><v-spacer /><ui-badge tone="neutral" :dot="false" :text="voteLabel(term.vote)" /></div>
            <div class="ui-cell-sub">{{ snapshot(term).registration }} · Classe {{ snapshot(term).class }}</div>
            <div class="ui-cell-sub">Registrado por {{ term.admin_name || 'Administração' }} · {{ term.created_at | defaultDate('DD/MM/YYYY HH:mm [BRT]') }}</div>
            <div v-if="term.source_term_id" class="ui-cell-sub">Termo #{{ term.source_term_id }} preservado no reaproveitamento dos votos.</div>
            <div v-if="term.plan_adhesion_term_id" class="ui-cell-sub"><v-icon x-small color="success">mdi-link-variant</v-icon> Adesão ao Plano · termo {{ term.plan_adhesion_code }} (assinatura GOV.br validada)</div>
            <v-btn small text color="primary" @click="download(term)"><v-icon small left>mdi-file-pdf-box</v-icon>Baixar termo</v-btn>
          </v-card>
        </div>
        <div class="ui-card__footer">
          <v-btn outlined :disabled="saving" @click="dialog = false">Fechar</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import axios from 'axios';
export default {
  props: ['votingId', 'clientId', 'status', 'subject'],
  data: () => ({ dialog: false, saving: false, loading: false, searching: false, syncing: false, error: '', credits: [], terms: [], creditId: null, vote: null, file: null, confirmed: false, search: '', sequence: 0,
    planAdhesion: null,
    planStatus: {
      ready: { label: 'Pronto para importar', color: 'blue' },
      synced: { label: 'Voto registrado', color: 'success' },
      partial: { label: 'Adesão parcial', color: 'orange' },
      already_voted: { label: 'Já votou', color: 'grey' },
      manual_term: { label: 'Termo manual', color: 'grey' },
      not_in_voting: { label: 'Fora da apuração', color: 'grey' },
      not_eligible: { label: 'Fora da continuação', color: 'grey' },
    },
    choices: [{text: 'Sim', value: 1}, {text: 'Não', value: 2}, {text: 'Abstenção', value: 3}] }),
  computed: {
    pendingSync() {
      if (!this.planAdhesion) return 0;
      return (this.planAdhesion.counts.ready || 0) + (this.planAdhesion.stale || 0);
    },
  },
  methods: {
    required: value => !!value || 'Obrigatório',
    creditLabel: item => `${item.name} · ${item.registration || ''} · Classe ${item.type}`,
    snapshot(term) { try { return JSON.parse(term.credit_snapshot); } catch (_) { return {}; } },
    voteLabel(vote) { return ['', 'Sim', 'Não', 'Abstenção'][Number(vote)]; },
    async open() { this.dialog = true; this.error = ''; await Promise.all([this.load(), this.loadPlanAdhesion(), this.searchCredits('')]); },
    async loadPlanAdhesion() {
      if (this.subject && this.subject !== 'plan') { this.planAdhesion = null; return; }
      try { this.planAdhesion = (await axios.get(`/api/admin/general-meeting-votings/${this.votingId}/plan-adhesions`)).data.content; }
      catch (error) { this.planAdhesion = null; this.error = this.$codes(error); }
    },
    async syncPlanAdhesions() {
      this.syncing = true; this.error = '';
      try {
        const result = (await axios.post(`/api/admin/general-meeting-votings/${this.votingId}/plan-adhesions/sync`)).data.content;
        this.$dialog.notify.success(`${result.imported} credor(es) importado(s)${result.removed ? `, ${result.removed} voto(s) removido(s) por adesão sem validade` : ''}.`, { position: 'top-right', timeout: 4000 });
        await Promise.all([this.load(), this.loadPlanAdhesion()]); this.$emit('saved');
      } catch (error) { this.error = this.$codes(error); }
      finally { this.syncing = false; }
    },
    async load() {
      this.loading = true;
      try { this.terms = (await axios.get(`/api/admin/general-meeting-votings/${this.votingId}/adhesion-terms`)).data.content; }
      catch (error) { this.error = this.$codes(error); }
      finally { this.loading = false; }
    },
    async searchCredits(name) {
      if (!this.clientId) return;
      const sequence = ++this.sequence; this.searching = true;
      try {
        const result = await axios.get('/api/admin/users-creditors', {params: {page: 1, limit: 50, search: {client_id: this.clientId, name: name || ''}}});
        if (sequence === this.sequence) this.credits = result.data.content.data;
      } catch (error) { this.error = this.$codes(error); }
      finally { if (sequence === this.sequence) this.searching = false; }
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true; this.error = '';
      try {
        const data = new FormData(); data.append('user_creditor_id', this.creditId); data.append('vote', this.vote); data.append('file', this.file);
        await axios.post(`/api/admin/general-meeting-votings/${this.votingId}/adhesion-terms`, data);
        this.$refs.form.reset(); await this.load(); this.$emit('saved');
      } catch (error) { this.error = this.$codes(error); }
      finally { this.saving = false; }
    },
    async download(term) {
      try {
        const result = await axios.get(`/api/admin/general-meeting-votings/${this.votingId}/adhesion-terms/${term.id}/file`, {responseType: 'blob'});
        const url = URL.createObjectURL(result.data), link = document.createElement('a');
        link.href = url; link.download = `termo-adesao-${term.id}.pdf`; link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (error) { this.error = this.$codes(error); }
    }
  }
};
</script>
